import { useEffect, useState } from 'react';
import { adminApi, type AdminAuditRow } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useErrorToast } from '@/lib/toast';
import { formatTime } from '@/util/format';

export function AuditPage() {
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');
  const [eventType, setEventType] = useState('');
  const [rows, setRows] = useState<AdminAuditRow[] | null>(null);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const onError = useErrorToast();

  async function search(reset: boolean) {
    setBusy(true);
    try {
      const r = await adminApi.searchAudit({
        orgId: orgId.trim() || undefined,
        userId: userId.trim() || undefined,
        eventType: eventType.trim() || undefined,
        before: reset ? undefined : (nextBefore ?? undefined),
        limit: 100,
      });
      setRows(reset ? r.data : [...(rows ?? []), ...r.data]);
      setNextBefore(r.nextBefore);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void search(true);
    // initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <p className="text-sm text-muted-foreground">
          Cross-org search across every audit event in the platform.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <Input
          placeholder="Filter by org id"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        />
        <Input
          placeholder="Filter by user or staff id"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <Input
          placeholder="Filter by event type (e.g. flow.create)"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
        />
        <Button disabled={busy} onClick={() => void search(true)}>
          Search
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Org</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>Metadata</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows?.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatTime(r.createdAt)}
                </TableCell>
                <TableCell className="font-mono text-xs">{r.eventType}</TableCell>
                <TableCell className="font-mono text-xs">{r.organizationId ?? '—'}</TableCell>
                <TableCell className="font-mono text-xs">
                  {r.adminUserId ?? r.userId ?? '—'}
                  {r.actorType && r.actorType !== 'user' && (
                    <Badge variant="secondary" className="ml-2 uppercase">
                      {r.actorType}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {r.resourceType ?? '—'}
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {r.resourceId ?? ''}
                  </div>
                </TableCell>
                <TableCell>
                  {r.metadata ? (
                    <pre className="max-h-48 max-w-md overflow-auto rounded-md border bg-muted/30 p-2 text-[11px] text-muted-foreground">
                      {JSON.stringify(r.metadata, null, 2)}
                    </pre>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No matching events.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {nextBefore && (
        <div>
          <Button variant="outline" disabled={busy} onClick={() => void search(false)}>
            Load older
          </Button>
        </div>
      )}
    </>
  );
}
